import * as React from "react";
import MainFrame from "../MainFrame";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Title from "../common/Title";
import { useNavigate } from "react-router-dom";
import { DepartmentType, Staff } from "../common/models";
import { addStaff } from "../common/api";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import { businessEntityId } from "../common/helper";
import { IUserContext, UserContext } from "../common/context";
import { DARK_COLOR } from "../common/constants";

interface Column {
  id: "name" | "department" | "hourlyRate" | "pin";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  { id: "name", label: "Name", minWidth: 150 },
  { id: "department", label: "Department", minWidth: 300 },
  { id: "hourlyRate", label: "Hourly Rate", minWidth: 15 },
  { id: "pin", label: "PIN", minWidth: 15 },
];
interface Data {
  token: string;
  name: string;
  location: string;
}

function createData(token: string, name: string, location: string): Data {
  return { token, name, location };
}

interface AddStaffModalProps {
  title: string;
  open: boolean;
  setClose: () => void;
}

function AddStaffModal(addStaffModalProps: AddStaffModalProps) {
  const [name, setName] = React.useState<string>();
  const [department, setDepartment] = React.useState<DepartmentType>();
  const [email, setEmail] = React.useState<string | null>();
  const [phoneNumber, setPhoneNumber] = React.useState<string | null>();
  const [hourlyRate, setHourlyRate] = React.useState(0);
  const [pin, setPin] = React.useState("");
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
  };
  return (
    <Modal open={addStaffModalProps.open} onClose={addStaffModalProps.setClose}>
      <Box sx={style}>
        <Title>{addStaffModalProps.title}</Title>
        <Grid container style={{ marginTop: 10 }}>
          <TextField
            required
            id="filled-required"
            label="Name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            style={{ width: 300 }}
          />
        </Grid>
        <Grid container style={{ marginTop: 15 }}>
          <FormControl style={{ width: 300 }}>
            <InputLabel style={{ minWidth: 500 }} id="test-select-label">
              Department
            </InputLabel>
            <Select
              fullWidth
              margin="dense"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={department}
              label="Dep"
              displayEmpty={true}
              onChange={(event) => {
                setDepartment(
                  DepartmentType[
                    event.target.value as keyof typeof DepartmentType
                  ]
                );
              }}
            >
              {Object.keys(DepartmentType).map((departmentType) => {
                return (
                  <MenuItem value={departmentType}>{departmentType}</MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid container style={{ marginTop: 10 }}>
          <TextField
            id="filled-required"
            label="Email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            style={{ width: 300 }}
          />
        </Grid>
        <Grid container style={{ marginTop: 10 }}>
          <TextField
            id="filled-required"
            label="Phone Number"
            value={phoneNumber}
            onChange={(event) => {
              setPhoneNumber(event.target.value);
            }}
            style={{ width: 300 }}
          />
        </Grid>
        <Grid container style={{ marginTop: 10 }}>
          <TextField
            required
            id="filled-required"
            label="Hourly Rate"
            value={hourlyRate}
            onChange={(event) => {
              setHourlyRate(Number(event.target.value));
            }}
            style={{ width: 300 }}
          />
        </Grid>
        <Grid container style={{ marginTop: 10 }}>
          <TextField
            required
            id="filled-required"
            label="Pin"
            value={pin}
            onChange={(event) => {
              setPin(event.target.value);
            }}
            style={{ width: 300 }}
          />
        </Grid>
        <Grid style={{ marginTop: 10, justifyContent: "flex-end" }} container>
          <Button
            variant="outlined"
            onClick={() => {
              if (!name || !department) {
                return;
              }
              addStaff(
                name,
                department,
                businessEntityId,
                hourlyRate,
                pin,
                email,
                phoneNumber
              ).then(() => {
                addStaffModalProps.setClose();
                setName("");
                setDepartment(DepartmentType.NAIL);
                setHourlyRate(0);
                setPin("");
                setEmail(null);
                setPhoneNumber(null);
              });
            }}
          >
            Save
          </Button>
        </Grid>
      </Box>
    </Modal>
  );
}

export default function MainPage() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [addStaffModalOpen, setAddStaffModalOpen] = React.useState(false);
  const navigate = useNavigate();

  const { staffs, refresh, setRefresh } =
    React.useContext<IUserContext>(UserContext);

  React.useEffect(() => {
    setRefresh(!refresh);
  }, [addStaffModalOpen]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <MainFrame>
      {" "}
      <React.Fragment>
        <Title>Staffs</Title>
        <Grid container>
          <Button
            style={{ color: DARK_COLOR, borderColor: DARK_COLOR }}
            variant="outlined"
            onClick={() => setAddStaffModalOpen(true)}
          >
            Add Staff
          </Button>
        </Grid>
        <Paper sx={{ width: "100%", overflow: "hidden", marginTop: 2 }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {staffs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((staff: Staff) => {
                    return (
                      <TableRow
                        onClick={() =>
                          navigate("/staffs/" + staff.id, { state: staff })
                        }
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={staff.id}
                      >
                        {columns.map((column) => {
                          const value = staff[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={staffs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </React.Fragment>
      <AddStaffModal
        open={addStaffModalOpen}
        setClose={() => setAddStaffModalOpen(false)}
        title="Add Staff"
      ></AddStaffModal>
    </MainFrame>
  );
}
